// @ts-nocheck
// Modal form for reviewers to submit structured feedback. Loads any
// previous draft (so they can come back) and writes to public.reviews.
// One row per user — submit replaces the previous response.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { REVIEW_QUESTIONS } from "../content/he/reviewer";

const styles = `
.rf-overlay { position: fixed; inset: 0; background: rgba(45,27,16,0.6); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding: 24px 16px; overflow-y: auto; backdrop-filter: blur(4px); }
.rf-modal { background: #FFFDF9; border-radius: 20px; padding: 32px 28px; max-width: 640px; width: 100%; box-shadow: 0 20px 60px rgba(92,61,46,0.35); direction: rtl; font-family: 'Heebo', sans-serif; position: relative; margin: 20px 0; }
.rf-close { position: absolute; top: 14px; left: 14px; background: none; border: none; cursor: pointer; font-size: 24px; color: #8B7355; line-height: 1; padding: 4px 10px; border-radius: 8px; }
.rf-close:hover { background: #F3EDE3; }
.rf-title { font-family: 'Frank Ruhl Libre', serif; font-size: 26px; font-weight: 700; color: #5C3D2E; margin-bottom: 8px; }
.rf-subtitle { font-size: 14px; color: #8B7355; margin-bottom: 24px; line-height: 1.6; }
.rf-question { margin-bottom: 24px; }
.rf-prompt { font-size: 15px; font-weight: 600; color: #5C3D2E; margin-bottom: 4px; display: block; }
.rf-hint { font-size: 12px; color: #8B7355; margin-bottom: 8px; }
.rf-textarea { width: 100%; padding: 12px 14px; border: 2px solid #E8DCC8; border-radius: 12px; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.7; color: #2D1B10; background: #FAF7F2; outline: none; min-height: 80px; resize: vertical; direction: rtl; }
.rf-textarea:focus { border-color: #D4875A; background: #FFFDF9; }
.rf-rating { display: flex; gap: 8px; direction: ltr; }
.rf-star { background: none; border: 2px solid #E8DCC8; cursor: pointer; padding: 8px 14px; border-radius: 10px; font-size: 16px; font-weight: 600; color: #8B7355; min-width: 44px; transition: all .15s; font-family: inherit; }
.rf-star.active { background: linear-gradient(135deg, #A0704A, #D4875A); border-color: #D4875A; color: white; transform: scale(1.05); }
.rf-star:hover:not(.active) { border-color: #D4875A; color: #5C3D2E; }
.rf-actions { display: flex; gap: 12px; margin-top: 28px; }
.rf-submit { flex: 1; background: linear-gradient(135deg, #A0704A, #D4875A); color: white; border: none; padding: 14px 24px; border-radius: 14px; font-family: inherit; font-size: 15px; font-weight: 600; cursor: pointer; }
.rf-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.rf-cancel { padding: 14px 24px; background: #F3EDE3; color: #5C3D2E; border: 1.5px solid #E8DCC8; border-radius: 14px; font-family: inherit; font-size: 15px; cursor: pointer; }
.rf-msg { font-size: 14px; line-height: 1.6; padding: 12px 14px; border-radius: 10px; margin-top: 12px; }
.rf-msg.success { background: #E8F4EC; color: #2E7D32; border-right: 3px solid #4CAF50; }
.rf-msg.error { background: #FFF0F0; color: #C62828; border-right: 3px solid #E53935; }
`;

type Props = { onClose: () => void; userId: string };

export function ReviewForm({ onClose, userId }: Props) {
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "saving" | "saved" | "error">(
    "loading"
  );
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("reviews")
        .select("responses")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (data?.responses) setResponses(data.responses);
      setStatus("idle");
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setAnswer = (id: string, val: any) =>
    setResponses((r) => ({ ...r, [id]: val }));

  const canSubmit = useMemo(() => {
    // require at least the rating + one written field
    return (
      typeof responses["overall_rating"] === "number" &&
      Object.values(responses).some(
        (v) => typeof v === "string" && v.trim().length > 0
      )
    );
  }, [responses]);

  const handleSubmit = async () => {
    setStatus("saving");
    setErrorMsg("");
    const { error } = await supabase
      .from("reviews")
      .upsert(
        { user_id: userId, responses, submitted_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    if (error) {
      setErrorMsg(error.message || "שגיאה בשמירת המשוב");
      setStatus("error");
      return;
    }
    setStatus("saved");
    setTimeout(onClose, 1500);
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="rf-overlay" onClick={onClose}>
        <div
          className="rf-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="rf-close" onClick={onClose} aria-label="סגירה">
            ×
          </button>
          <div className="rf-title">משוב על הספר</div>
          <div className="rf-subtitle">
            תודה שאתם משקיעים את הזמן. כל מה שתכתבו יעזור לי לזקק את הספר.
            אפשר לחזור ולערוך בכל עת.
          </div>

          {REVIEW_QUESTIONS.map((q) => (
            <div className="rf-question" key={q.id}>
              <label className="rf-prompt" htmlFor={`q_${q.id}`}>
                {q.prompt}
              </label>
              {q.hint && <div className="rf-hint">{q.hint}</div>}
              {q.kind === "rating" ? (
                <div className="rf-rating" role="radiogroup">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={responses[q.id] === n}
                      className={`rf-star${responses[q.id] === n ? " active" : ""}`}
                      onClick={() => setAnswer(q.id, n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  id={`q_${q.id}`}
                  className="rf-textarea"
                  value={responses[q.id] || ""}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  placeholder="כתבו כאן…"
                />
              )}
            </div>
          ))}

          <div className="rf-actions">
            <button
              className="rf-submit"
              disabled={!canSubmit || status === "saving"}
              onClick={handleSubmit}
            >
              {status === "saving" ? "שומר…" : "שליחת המשוב"}
            </button>
            <button className="rf-cancel" onClick={onClose}>
              סגירה
            </button>
          </div>

          {status === "saved" && (
            <div className="rf-msg success">
              <strong>תודה רבה!</strong> המשוב שלכם נשמר.
            </div>
          )}
          {status === "error" && (
            <div className="rf-msg error">{errorMsg}</div>
          )}
        </div>
      </div>
    </>
  );
}
